import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Divider, Drawer, Grid, IconButton, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icons, SearchInput, StatusDot } from 'greener-ui-library';
import { useLocalStorage } from 'hooks/useLocalStorage';
import useUnitsConnectionStatus from 'hooks/useUnitsConnectionStatus';
import { getAssetLabel } from 'utils/formatters';
import { getUnitTypeLabel } from 'utils/units';
import { saveRecentSearch } from './globalSearch.utils';
import {
  SearchDrawerProps,
  SearchResultHeaderProps,
  SearchResultItemData,
  SearchResultItemProps,
  SearchResultProps,
  SearchResultType,
} from './types';
import { useGlobalSearch } from './useGlobalSearch';

const PREFIX = 'SearchDrawer';

const classes = {
  drawer: `${PREFIX}-drawer`,
};

const StyledDrawer = styled(Drawer)(() => ({
  [`& .${classes.drawer}`]: {
    width: 375,
    maxWidth: '100%',
  },
}));

function SearchResultHeader({ title }: SearchResultHeaderProps) {
  return (
    <Typography mb={2} variant={'subtitle2'}>
      {title}
    </Typography>
  );
}

function SearchResultItem({
  title,
  description,
  link,
  externalIdentifier,
  type,
  closeDrawer,
  setRecentSearches,
  isOnline,
}: SearchResultItemProps) {
  const navigate = useNavigate();

  return (
    <Grid container direction="row" spacing={1} justifyContent="center">
      <Grid item sx={{ width: '100%', mb: 1 }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            backgroundColor: (theme) => theme.palette.background.grey,
            px: 2,
            py: 1.5,
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          onClick={() => {
            navigate(link);
            closeDrawer();
            setRecentSearches((prev) => saveRecentSearch(prev, { title, description, link, externalIdentifier, type }));
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2">{title}</Typography>
            {typeof isOnline === 'boolean' && <StatusDot color={isOnline ? 'primary.main' : 'grey.300'} />}
          </Box>
          <Typography variant="caption" sx={{ color: 'grey.600' }}>
            {description}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

const SearchResult = ({ isFetching, searchString, data, handleClose }: SearchResultProps) => {
  const { t } = useTranslation();
  const [recentSearches, setRecentSearches] = useLocalStorage('recentSearches', [] as SearchResultItemData[]);
  const unitStatuses = useUnitsConnectionStatus();

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress data-testid="loading-indicator" />
      </Box>
    );
  }

  if (searchString === '') {
    return (
      <>
        <Typography mb={2} variant="body2" color="textSecondary">
          {t('main:search.recentSearches')}
        </Typography>
        <Box mb={1}>
          {recentSearches.map((searchItem) => (
            <SearchResultItem
              key={searchItem.title}
              title={searchItem.title}
              description={searchItem.description}
              link={searchItem.link}
              externalIdentifier={searchItem.externalIdentifier}
              type={searchItem.type}
              closeDrawer={handleClose}
              setRecentSearches={setRecentSearches}
              isOnline={
                [SearchResultType.ASSET].includes(searchItem.type)
                  ? !!unitStatuses[searchItem.externalIdentifier!]
                  : undefined
              }
            />
          ))}
        </Box>
      </>
    );
  }

  const hasNoResults = data?.projects?.length === 0 && data?.assets?.length === 0 && data?.bookedAssets?.length === 0;

  if (hasNoResults) {
    return (
      <Typography variant="body2" color="textSecondary">
        {t('main:search.noResults')}
      </Typography>
    );
  }

  return (
    <>
      {data?.assets.length > 0 && <SearchResultHeader title={t('main:navigation.units')} />}
      <Box mb={1}>
        {data?.assets.map((unit) => (
          <SearchResultItem
            key={unit.externalIdentifier}
            title={getAssetLabel(unit)}
            description={getUnitTypeLabel(t, unit.deviceType?.deviceTypeId)}
            link={'/assets/' + unit.externalIdentifier}
            externalIdentifier={unit.externalIdentifier}
            type={SearchResultType.ASSET}
            closeDrawer={handleClose}
            setRecentSearches={setRecentSearches}
            isOnline={!!unitStatuses[unit.externalIdentifier]}
          />
        ))}
      </Box>

      {data?.projects.length > 0 && <SearchResultHeader title={t('main:navigation.projects')} />}
      <Box mb={1}>
        {data?.projects.map((project) => (
          <SearchResultItem
            key={project.externalIdentifier}
            title={project.name}
            description={t('entities:project.nameSingular')}
            link={'/projects/' + project.externalIdentifier}
            externalIdentifier={project.externalIdentifier}
            type={SearchResultType.PROJECT}
            closeDrawer={handleClose}
            setRecentSearches={setRecentSearches}
          />
        ))}
      </Box>

      {!!data?.bookedAssets?.length && <SearchResultHeader title={t('main:search.bookings')} />}
      <Box mb={1}>
        {data?.bookedAssets?.map((booking) => (
          <SearchResultItem
            key={`${booking.unit.greenerUnitId}${booking.projectName}`}
            title={booking.projectName}
            description={`${booking.unit.greenerUnitExternalIdentifier} | ${booking.unit.greenerUnitName} ${t(
              'main:search.unitIsBooked'
            )}`}
            link={'/projects/' + booking.projectExternalIdentifier}
            externalIdentifier={booking.unit.greenerUnitExternalIdentifier}
            type={SearchResultType.BOOKED_ASSET}
            closeDrawer={handleClose}
            setRecentSearches={setRecentSearches}
          />
        ))}
      </Box>
    </>
  );
};

export default function SearchDrawer({ isSearchDrawerOpen, setIsSearchDrawerOpen, ...props }: SearchDrawerProps) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState('');
  const location = useLocation();

  useEffect(() => {
    setIsSearchDrawerOpen(false);
  }, [location.pathname]);

  const { data, isFetching } = useGlobalSearch({
    searchTerm: searchString,
  });

  const clearInput = () => {
    setSearchString('');
  };

  const close = () => {
    setIsSearchDrawerOpen(false);
    clearInput();
  };

  const onSearchChange = (value) => {
    setSearchString(value);
  };

  return (
    <StyledDrawer
      data-testid="search-drawer"
      anchor="right"
      classes={{ paper: classes.drawer }}
      ModalProps={{ BackdropProps: { invisible: true } }}
      onClose={close}
      open={isSearchDrawerOpen}
      variant="temporary"
      {...props}
    >
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" color="textPrimary">
            {t('main:navigation.search')}
          </Typography>
          <IconButton onClick={close}>
            <Icons.Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box>
          <SearchInput
            fullWidth
            placeholder={t('main:search.placeholder')}
            onDebouncedChange={onSearchChange}
            onClear={clearInput}
            autoFocus
          />
        </Box>
        <Box mt={2} data-testid="search-results">
          <SearchResult isFetching={isFetching} searchString={searchString} data={data} handleClose={close} />
        </Box>
      </Box>
    </StyledDrawer>
  );
}
